import { useState, useEffect, useRef, useCallback } from 'react'
import { Loader2, FileQuestion, AlertTriangle, Trash2, MoreVertical, Save, FolderSearch, Download, LayoutGrid, RotateCcw, Clock } from 'lucide-react'
import { CommandRegistryProvider, CoverCommandScope, EditorCoverProvider, type EditorCover, formatShortcut, Tooltip, AppTopBar, ShellTopBarPortal, useUndo, useToast, useTheme, useConfirm } from '@papan/core-ui/ui'
import { isMountId } from '@papan/files/ui/protected-branches'
import { pointerKindOf, ACCESS_CHANGE_MIN_INTERVAL_MS } from '@papan/types'

/** How many loads in a row the window sets aside as superseded before it shows one (#1260). */
const SUPERSEDED_REASKS = 2
import { usePageTitle } from '@papan/brand/react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { filesApi, reportListedRowRefused } from '@papan/files/client'
import { accessGenerationNow, fetchAndCacheFileInfo, fetchFileInfoPastAccessChanges, fileKeys, seedPermission, useIdentitySwitchEpoch } from '@papan/files/client/queries'
import { useQueryClient } from '@tanstack/react-query'
import { request, actingOrgEmailOf, isSessionEndedError, errorText } from '@papan/api-client'
import type { EditorEditHandle, RevealLineRequest } from '@papan/collab/client'
import { EDITOR_SESSION_ENDED_EVENT, type EditorSessionEndedDetail } from '@papan/collab/client/session-events'
import { useRealtime } from '@papan/realtime/client'
import { useNewProject, useDeleteRunner } from '@papan/files/ui'
import { OpenAsProjectDialog } from './OpenAsProjectDialog'
import { SaveAsDialog } from './SaveAsDialog'
import { VersionPanel } from './VersionPanel'
import { VersionPreview } from './VersionPreview'
import { EditorHost } from './EditorHost'
import { chainAwaitsApprovalInListing } from './pending-chain-check'
import { detailIsBack } from './tab-ancestry'
import { useTranslation } from '@papan/i18n'
import { useSaveBlockedNotice, PREVIEW_SAVE_BLOCKED_KEY } from './useSaveBlockedNotice.js'

function getFileExt(name: string): string {
  const dotIdx = name.lastIndexOf('.')
  return dotIdx > 0 ? name.slice(dotIdx + 1).toLowerCase() : ''
}

const LoadingSpinner = (
  <div className="flex-1 flex items-center justify-center">
    <Loader2 className="animate-spin themed-text-muted" size={24} />
  </div>
)

interface SingleFileViewProps {
  tenantId: string
  /** Phone viewport: drop the breadcrumb PATH (it overflows a narrow screen and
   *  duplicates the MobileHeader's context) and slim the toolbar to just the
   *  file's actions. */
  mobile?: boolean
  /** Embedded mode (chat home side panel): render THIS file instead of the
   *  `/file/:fileId` route param, and close via the callback instead of
   *  history navigation. */
  fileId?: string
  onClose?: () => void
  /** Embedded mode: Save-As lands here (the host swaps its fileId) instead
   *  of navigating to `/file/:id`. */
  onSwitchFile?: (fileId: string) => void
  /** Embedded mode: bubble the editor's unsaved state up so the host (chat
   *  home) can badge its context chip. Mirrors the internal `isDirty`. */
  onDirtyChange?: (dirty: boolean) => void
  /** Embedded mode: forwarded to the mounted editor so the host can pull its
   *  `getSelection()` (design-mode element / text range) for the AI chip. */
  editorRef?: React.Ref<EditorEditHandle>
  /** Is the surface hosting this view on screen? Its own route always is, but
   *  the chat home embeds it and that deck stays MOUNTED when hidden — without
   *  this, a file left open there would name the tab from behind the drive. */
  deckActive?: boolean
  /** #249: a line to show (a chat link `file:42`); text editors scroll to it and flash it. */
  revealLine?: RevealLineRequest
}

export function SingleFileView({ tenantId, mobile, fileId: fileIdProp, onClose, onSwitchFile, onDirtyChange, editorRef, deckActive = true, revealLine }: SingleFileViewProps) {
  // #1210: an org-email switch reloads the open file (and its version preview) as the identity now active
  const identityEpoch = useIdentitySwitchEpoch()
  // Opening a file on its own is a SECOND way into every editor, beside the
  // workspace tab. Without this the editors mounted here register no undo at
  // all, so Ctrl+Z reaches the drive's file-operation stack and reverses an
  // unrelated delete — the failure the editors' own undo exists to remove.
  // Deck-gated like the workspace's isActive: a hidden deck must not claim
  // the keystroke from whatever the user is actually looking at.
  const { registerEditor, setUndoRegion, recordDeleteResult } = useUndo()
  const { t } = useTranslation('files')
  const { t: tCollab } = useTranslation('collab')
  // This view IS the editor — there is no explorer beside it to click into,
  // so the region is fixed for as long as it is the visible deck.
  useEffect(() => {
    if (deckActive) setUndoRegion('editor')
  }, [deckActive, setUndoRegion])
  const { fileId: fileIdParam } = useParams<{ fileId: string }>()
  const fileId = fileIdProp ?? fileIdParam
  const navigate = useNavigate()
  const location = useLocation()
  const urlPrefix = tenantId.startsWith('p_') ? '/~' : `/t/${tenantId}`
  const { onFileEvent, onResync, onBaseline } = useRealtime()
  const { settings } = useTheme()
  const [fileName, setFileName] = useState<string | null>(null)
  const [fileParentId, setFileParentId] = useState<string | null>(null)
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ id: string; name: string }>>([])
  const [error, setError] = useState<string | null>(null)
  const [deleted, setDeleted] = useState(false)
  const breadcrumbsRef = useRef(breadcrumbs)
  breadcrumbsRef.current = breadcrumbs
  /**
   * The file THIS window asked to delete, by id. Only the wording differs —
   * an own delete and somebody else's land on the same screen.
   *
   * Not a `useState` boolean set when the request SUCCEEDS, which is what it
   * was: the screen is gated on `deleted`, and `deleted` comes from the
   * realtime broadcast, which routinely beats the HTTP answer. So for as long
   * as your own delete was in flight the screen read "was deleted while you
   * had it open" — the sentence reserved for somebody ELSE's delete.
   *
   * A REF, so it can be written before the request without causing a render
   * (which is the whole point — nothing should flash on the way), and keyed by
   * ID rather than a flag so it can never be read for a file it was not made
   * for. The old boolean WAS reset on a file swap by the effect below (this is
   * not the bug that was fixed, and a reader should not conclude otherwise);
   * an id compared to the live `fileId` just needs no reset to be right,
   * including in the render before an effect has run.
   */
  const askedToDeleteRef = useRef<string | null>(null)
  /** The row's pointer kind, for the share-slot warning below. By KIND and not
   *  by name: a `.shared`-kind row holds its slot whatever it is called (I110),
   *  which is the drive's own reason for reading it this way. */
  const [fileKind, setFileKind] = useState<string | null>(null)
  // D24: the file is a `.chain` that still Needs approval — nobody follows it, so it
  // doesn't open here either (a deep link / bookmark to it); say why instead.
  const [chainPending, setChainPending] = useState(false)
  /**
   * Everything above is a verdict about ONE file, and this view is reused for
   * the next one: the desktop and mobile routes mount it with no key (the two
   * embedded hosts pass `key={fileId}`, which is why this never showed there).
   * Without this reset, deleting file A and then opening file B in the same
   * window showed B under A's verdict — "File deleted", "B is in the bin", for
   * a file nobody had touched.
   */
  useEffect(() => {
    setDeleted(false)
    setError(null)
    setChainPending(false)
    // `fileKind` with them: the ⋮ Delete reads it to decide whether to warn
    // that this is a live `.shared` whose deletion changes who has access. Left
    // stale across a swap it warns about the wrong row — or, worse, skips the
    // warning for a new row that needs it.
    setFileKind(null)
    // And the NAME, which is what gates everything else on this screen: while
    // it is null the view is a spinner, so there is no ⋮ and no Delete until
    // the new row has actually answered. Clearing `fileKind` without clearing
    // this traded one hazard for a worse one — the menu stayed live with A's
    // name over B's id, so a `.shared` → `.shared` swap dropped the
    // access-changes warning, the confirm named the previous file, and the undo
    // entry was filed under that name. `breadcrumbs` and `fileParentId` are
    // deliberately NOT cleared with it: while the name is null nothing reads
    // them (the failure shells carry their own bar), so clearing them would be
    // a change nothing can observe.
    setFileName(null)
  }, [fileId])
  const [isDirty, setIsDirty] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [showSaveAs, setShowSaveAs] = useState(false)
  const [showProjectPicker, setShowProjectPicker] = useState(false)
  const newProject = useNewProject()
  const [showVersionPanel, setShowVersionPanel] = useState(false)
  const [previewVersionId, setPreviewVersionId] = useState<string | null>(null)

  /** Exactly the overlay's own render condition, so the two cannot drift. */
  const previewOpen = !!previewVersionId && showVersionPanel
  /** #930 — the editor's own cover (the conflict "Saved version" panel), reported through EditorCoverProvider. */
  const [editorCover, setEditorCover] = useState<EditorCover | null>(null)
  const editorCoverRef = useRef(editorCover)
  editorCoverRef.current = editorCover
  /** SOMETHING covers the editor — the preview or its own cover. What every Save gate here reads; see EditorCover. */
  const editorCovered = previewOpen || editorCover !== null

  // The editor's undo registration is withheld while a VERSION PREVIEW covers
  // it. That overlay is `absolute inset-0` over a still-mounted, still-
  // editable live editor, and the region is already pinned to 'editor' — so
  // Ctrl+Z while reading an old version silently reverted a step in the LIVE
  // file, with the user looking at a page that did not change.
  //
  // `TabContent` guards the workspace path the same way, in the same shape, on
  // purpose: changing one should change the other. An earlier version of this
  // comment claimed it already did while it did not, and that claim is why the
  // workspace path stayed broken — an audit of either file concluded the other
  // was covered.
  const registerUndoRedo = deckActive && !previewOpen ? registerEditor : undefined
  const toast = useToast()
  const noticeSaveBlocked = useSaveBlockedNotice(toast, previewOpen ? 'preview' : editorCover)
  /** Read inside the Cmd+S listener. A ref because the listener's deps do not
   *  include the preview — rebinding it on every preview change would be
   *  churn, and the handler only ever wants the value at keystroke time. */
  const previewOpenRef = useRef(previewOpen)
  previewOpenRef.current = previewOpen
  /** Read inside the Cmd+S listener; a ref because its deps do not include
   *  the dirty flag. */
  const isDirtyRef = useRef(isDirty)
  isDirtyRef.current = isDirty

  // ...but SOMETHING still has to claim the keystroke while the preview is
  // up, or it leaves the tab and reaches the drive's file history — pressing
  // Ctrl+Z over an old version would restore a file deleted minutes ago.
  // Claims and does nothing, which is the honest answer for a read-only view.
  useEffect(() => {
    if (!previewOpen || !deckActive) return
    return registerEditor({
      editable: true,
      undo: async () => false,
      redo: async () => false,
      onStateChange: (cb) => { cb({ canUndo: false, canRedo: false }); return () => {} },
    })
  }, [previewOpen, deckActive, registerEditor])
  const [previewIsLatest, setPreviewIsLatest] = useState(false)
  const [closing, setClosing] = useState(false)
  const queryClient = useQueryClient()
  const menuRef = useRef<HTMLDivElement>(null)
  const saveRef = useRef<(() => void) | null>(null)
  const discardRef = useRef<(() => void) | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(closeTimer.current), [])

  // The open file names the tab, and it sits ABOVE its host in the title
  // stack — mount order — so a file open in the chat home wins over the chat's
  // own name, which is what you want: the tab says what you are looking at.
  usePageTitle(fileName, deckActive)

  const handleDirtyChange = useCallback((dirty: boolean) => {
    setIsDirty(dirty)
    onDirtyChange?.(dirty)
  }, [onDirtyChange])
  // (#942: the read-only decision, its live flip and the dirty reset that goes
  // with it live in EditorHost, shared with the workspace tab.)

  /**
   * Open this file inside a project workspace — the explorer, tabs and the AI
   * panel — finding an existing one or wrapping the file's parent folder.
   *
   * This was the DEFAULT until a drive double-click stopped opening a
   * workspace, and it is now the opt-in. It is a visible button rather than a
   * ⋮ entry for that reason: it is the one thing this window cannot show you,
   * so burying it in a menu would make the surface with the AI unreachable
   * except by people who go looking.
   */
  const openAsProject = useCallback(() => {
    setShowMenu(false)
    setShowProjectPicker(true)
  }, [])

  // The three answers the picker can give. Each one was previously a BRANCH the
  // resolver took on the user's behalf, which is why the same click could open
  // an existing project, silently mint a new one named after a folder, or wrap
  // the drive root — with nothing on screen distinguishing them.
  const openExistingProject = useCallback((workspaceFolderId: string) => {
    setShowProjectPicker(false)
    navigate(`${urlPrefix}/workspace/${workspaceFolderId}?open=${fileId}`)
  }, [fileId, navigate, urlPrefix])

  /** Wrap a plain folder as a workspace — unchanged `/open-folder` behaviour
   *  (create-or-open the deterministic ws_/wsi_ and link the folder, so the
   *  explorer shows the file in context), now reached only by choosing it. */
  const wrapFolderAsProject = useCallback(async (
    parentFolderId: string,
    parentFolderName: string,
    scope?: 'individual' | 'organization',
  ) => {
    setShowProjectPicker(false)
    try {
      const ws = await request<{ folderId: string }>('/api/workspaces/open-folder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderId: parentFolderId, folderName: parentFolderName, scope }),
      })
      navigate(`${urlPrefix}/workspace/${ws.folderId}?open=${fileId}`)
    } catch {
      navigate(`${urlPrefix}/workspace?open=${fileId}`)
    }
  }, [fileId, navigate, urlPrefix])

  /** Hand off to the shared New Project dialog — audience and placement
   *  (My Space vs the organization) keep their single implementation there. */
  const createProjectForFile = useCallback(() => {
    setShowProjectPicker(false)
    newProject.open({
      onDone: (folderId) => navigate(`${urlPrefix}/workspace/${folderId}?open=${fileId}`),
    })
  }, [newProject, fileId, navigate, urlPrefix])

  /**
   * Delete this file. The drive's own runner does the work — one progress
   * toast, big folders followed as background jobs, the failure wording in the
   * person's language, and the undo entry — so this screen does not invent a
   * second way to delete something.
   *
   * It does NOT navigate away afterwards. Two attempts at that are worth
   * recording: awaiting `deleteItems` and leaving after it, and leaving from
   * the runner's `onDeleted` — neither moved, URL unchanged. Season then said
   * the resulting screen is what he wants anyway, because it confirms the file
   * really went, so the exit is gone rather than left in half-working.
   *
   * (Why neither fired is still open, and it matters beyond this: that screen's
   * own "Go back" is the same `closeView`, and it was seen doing nothing once
   * and working once — issue #1011.)
   */
  const { deleteItems } = useDeleteRunner({
    // The runner does NOT record undo — it calls this, and the caller writes
    // the entry. The drive does it here too (`recordDeleteUndo`), as do the
    // workspace tree and the project cards. Without it this Delete joined the
    // phone's Drive delete (`MobileDriveScreen`) as one you could not take
    // back — and that one is still like that, which is filed, not fixed here.
    //
    // Two cases it must not record, both straight from the drive's version: a
    // delete that landed in ANOTHER org's bin, and a split through a mount
    // that binned source-tenant ids this drive's undo cannot resolve.
    onDeleted: (target, outcome, at) => {
      if (!at || outcome.deletedInSource) return
      if (outcome.binId === undefined && isMountId(target.id)) return
      recordDeleteResult(at, target.id, target.name, outcome)
    },
  })
  const confirm = useConfirm()
  const deleteFile = useCallback(async () => {
    if (!fileId) return
    const name = fileName ?? 'this file'
    // Deleting a live `.shared` frees its slot, which silently promotes a
    // demoted "Conflict" copy in its place and can CHANGE WHO HAS ACCESS. The
    // drive folds that into its confirm; asking without it here would be the
    // one delete in the product that changes access without saying so. The
    // preview is advisory — a failure must not block the delete.
    let detachNote = ''
    if (pointerKindOf({ fileType: fileKind }) === 'shared') {
      try {
        const preview = await filesApi.promotePreview(fileId)
        if (preview.willPromote && preview.promote?.name) {
          detachNote = ' ' + t('driveView.confirm.deleteSharePromote', { promoteName: preview.promote.name.replace(/\.shared$/i, '') })
        }
      } catch { /* advisory */ }
    }
    const confirmed = await confirm({
      title: t('driveView.confirm.deleteTitle'),
      message: t('driveView.confirm.deleteFile', { name }) + detachNote,
      type: 'danger',
      confirmText: t('driveView.menu.delete'),
    })
    if (!confirmed) return
    // Recorded BEFORE the request, so the wording is right from the moment the
    // screen appears — and dropped again if the delete is refused, so a later
    // delete by somebody else is not reported as yours.
    askedToDeleteRef.current = fileId
    const [outcome] = await deleteItems([{ id: fileId, name }])
    if (!outcome?.ok && askedToDeleteRef.current === fileId) askedToDeleteRef.current = null
  }, [fileId, fileName, fileKind, confirm, deleteItems, t])

  /** Leave this view when there is no host to hand back to.
   *
   *  `navigate(-1)` alone is a dead end for anyone who arrived WITHOUT an
   *  in-app history entry — a bookmark, a pasted link, a fresh tab — where it
   *  silently does nothing and the control looks broken. Do NOT reach for
   *  `history.length` instead: measured in this app, a fresh tab already
   *  reports 2, so it claims a back entry that is not there. */
  const goBack = useCallback(() => {
    // `location.key !== 'default'` says this entry was pushed by the router —
    // NOT that there is anything behind it. Opening a file in a fresh tab and
    // landing here through one in-app navigation satisfies it, and `navigate(-1)`
    // then fails silently: no error, no movement, a dead button. Season hit
    // exactly that on this screen's "Go back", and it is why two attempts to
    // leave automatically after a delete also did nothing.
    //
    // React Router keeps its own position in `history.state.idx`, and 0 means
    // first entry — nothing to go back to. The key check stays as the fallback
    // for a state this app did not write (a hard navigation into the route).
    //
    // This fixes the dead button, not a second thing found beside it: in a
    // long-lived session the FIRST back is ignored and the second works. That
    // one is not ours — instrumented end to end, the browser's own
    // `history.back()` is dropped just the same, with no popstate and nothing
    // in the app pushing anything back. Filed.
    const idx = (window.history.state as { idx?: number } | null)?.idx
    // `Number.isInteger`, not `typeof === 'number'`: a raw
    // `history.replaceState({}, …)` elsewhere in the app (the billing page does
    // it on a Stripe return) wipes the router's `idx` off that entry, and every
    // push after it carries `NaN`. `NaN` is a number and `NaN > 0` is false, so
    // the loose check answered "nothing behind us" for the rest of the tab's
    // life and this control jumped to /home instead of going back.
    const canGoBack = Number.isInteger(idx) ? (idx as number) > 0 : location.key !== 'default'
    if (canGoBack) { navigate(-1); return }
    navigate(`${urlPrefix}/home`)
  }, [location.key, navigate, urlPrefix])

  // Close: on mobile, play the slide-DOWN exit (mirror of the open slide-up)
  // before actually navigating away, so the view animates off the bottom.
  const closeView = () => {
    if (onClose) { onClose(); return }
    if (!mobile) { goBack(); return }
    setClosing(true)
    closeTimer.current = setTimeout(goBack, 260)
  }

  // Ctrl+S / Cmd+S to save.
  //
  // Gated on `deckActive`: this listens on `document`, so
  // a HIDDEN instance would answer the shortcut too. The chat home keeps this
  // view mounted behind `display:none` (both its deck and, when narrow, its
  // Files page), so without this a Cmd+S typed into the chat composer saved a
  // file that was not on screen. Same reasoning as the page title above.
  // (A read-only editor gets no save hook from EditorHost, so `saveRef` stays
  // empty and the shortcut does nothing.)
  useEffect(() => {
    if (!deckActive) return
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        // Not while a version preview covers the editor. The file underneath
        // is still mounted and still dirty, so this saved it invisibly — the
        // same blindness the undo guard above fixes, in the other shortcut.
        //
        // See `useSaveBlockedNotice` for the rules and for what is
        // deliberately left unfixed.
        if (previewOpenRef.current) {
          noticeSaveBlocked(isDirtyRef.current, () => setPreviewVersionId(null))
          return
        }
        // #930 — the editor covers itself (the conflict "Saved version" panel): refused with its own notice.
        const cover = editorCoverRef.current
        if (cover) {
          noticeSaveBlocked(isDirtyRef.current, cover.uncover, cover)
          return
        }
        saveRef.current?.()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [deckActive, noticeSaveBlocked])

  /**
   * Bumped to ask THIS effect to load the file again — the only way anything
   * triggers a reload.
   *
   * The restore branch below used to run its own `fetchAndCacheFileInfo` and
   * write the answer straight into state, and two separate holes came out of
   * that one decision: a reload that FAILED had nowhere to put the error (its
   * `.catch` swallowed it, leaving a bare spinner with no title bar and no way
   * out at all — strictly worse than the error screen it replaced), and a
   * reload whose answer arrived AFTER the window had swapped to another file
   * wrote the old file's name, kind and parent over the new one. This effect
   * already solves both, because it is keyed on `fileId` and guards its own
   * writes with `cancelled`. So the restore branch asks for a reload instead
   * of performing one.
   */
  const [reloadSeq, setReloadSeq] = useState(0)
  /** consecutive loads set aside as superseded by an access change (#1260) — capped, per file */
  const supersededAsksRef = useRef(0)
  const supersededForRef = useRef(fileId)
  if (supersededForRef.current !== fileId) { supersededForRef.current = fileId; supersededAsksRef.current = 0 }
  /**
   * A reload the restore branch has asked for, consumed once by the loader.
   *
   * `fromLiveEditor` is what the window was showing WHEN THE RELOAD WAS ASKED
   * FOR, not when its answer arrives — the difference decides where a failure
   * goes. Asked for over a live editor, a failure must stay a toast: the error
   * screen returns before the editor is built, so it would unmount the editor
   * and drop the collab room with anything unsaved in it. Asked for from the
   * deleted receipt, there is no editor and nothing to lose, and the restore
   * branch has already cleared `deleted` — so a failure must say "Unable to
   * open" rather than leave the reader on a stale editor behind a toast that
   * dismisses itself.
   */
  const pendingReloadRef = useRef<{ fromLiveEditor: boolean } | null>(null)
  /** Whether a live editor is on screen right now — exactly the condition the
   *  render path below uses to get there. Read by the restore branch BEFORE it
   *  clears `deleted`, which is why it is a ref and not recomputed there: the
   *  realtime effect does not re-run on these, so its closure's copies are
   *  stale. */
  const editorLiveRef = useRef(false)
  editorLiveRef.current = !error && !(deleted && askedToDeleteRef.current === fileId) && !chainPending && !!fileName
  /** #823: whether a delete is on screen at all — this window's own receipt, or someone else's strip over the kept
   *  editor. */
  const deletedShownRef = useRef(false)
  deletedShownRef.current = deleted
  /** #823: the editor's live session was ended by the server (collab 4410, a 4403 refusal, office `oo-closed`) — for
   *  good: only a NEW editor instance joins the file again. THE RULE: while it is ended the window asks the server
   *  again on every restore (whatever it names — a pointer's restore names its TARGET) and every reload, and only
   *  the server's "back" answer (`fileIsBack`) clears the deleted state and mounts a fresh editor (the detail is then
   *  in the cache the new host reads). A restore event alone decides nothing. An editor that was never ended is never
   *  remounted: it may hold offline edits. */
  const editorEndedRef = useRef(false)
  /** Asks the server whether the file is back (the probe below); a no-op unless the editor is ended. */
  const probeIfEndedRef = useRef<() => void>(() => {})
  /** A fresh editor was mounted since the last restore / reload ask — a refusal now waits for the next one. */
  const remountedFromRefusalRef = useRef(false)
  const [restoredSeq, setRestoredSeq] = useState(0)
  /** Counts the deletes this window has applied: an answer read BEFORE the latest one says nothing about now
   *  (binned, restored, binned again while the read was out — its late 200 must not remount). */
  const deleteSeqRef = useRef(0)
  /** The ONE "the server says this file is back" step, for the restore reload and the end-of-session probe alike:
   *  only an answer read after the latest delete, and only a live one (a pointer whose target is in the bin answers
   *  200 too). Clears a "deleted by someone else" strip (the restore event may come later, or not at all after a
   *  socket gap) and mounts a fresh editor if the old one's session was ended. */
  const fileIsBack = useCallback((info: unknown, readAtDeleteSeq: number) => {
    if (readAtDeleteSeq !== deleteSeqRef.current || !detailIsBack(info)) return
    // read after the delete on screen was applied, and live: restored (this window's own delete included — its
    // receipt goes too). Before any delete was applied there is nothing to clear, and this window's own delete
    // still in flight must keep its record, or its receipt would show as someone else's strip.
    if (deletedShownRef.current) {
      askedToDeleteRef.current = null
      setDeleted(false)
    }
    if (!editorEndedRef.current) return
    editorEndedRef.current = false
    remountedFromRefusalRef.current = true
    setRestoredSeq(n => n + 1)
  }, [fileId])
  /** `toast` kept in a ref so the loader need not take it as a dep. Same pattern
   *  as `previewOpenRef` / `isDirtyRef` above. (`ToastProvider` memoises its
   *  value, so a dep would be stable today — the ref keeps that from being
   *  something the loader quietly depends on.) */
  const toastRef = useRef(toast)
  toastRef.current = toast
  useEffect(() => {
    if (!fileId) return
    // Single round trip — detail endpoint inlines breadcrumbs + effectivePermission
    // so SingleFileView no longer fans out to /breadcrumbs and /permission.
    let cancelled = false
    const reload = pendingReloadRef.current
    pendingReloadRef.current = null
    // A reload asked for by the restore branch has to be a genuinely NEW read,
    // and each of the two steps is here for its own measured reason (all three
    // numbers below came from running it against a real QueryClient):
    //
    // `invalidateQueries` alone is enough ONCE THE ENTRY HAS SETTLED — reading
    // again while it is fresh answers from the cache and asks nothing, and
    // marking it stale fixes that. What invalidating does NOT do is restart a
    // request already IN FLIGHT: `fetchQuery` joins that one instead. Which is
    // the case that mattered — a restore arriving while the first load was still
    // out got answered by that very request, which had 404'd because the file
    // was in the bin at the time, so the reader stayed on "Unable to open" for a
    // file that is live in the drive. Hence `cancelQueries` first.
    //
    // And NOT `removeQueries`, which would also force the read: this entry is
    // what `EditorHost`'s own `useFile` reads, and its unavailable rule turns on
    // `!data` precisely so "a failed BACKGROUND refetch keeps the last good data
    // and must never tear down an open editor" (its words). Dropping the entry
    // deletes that data, so a failed reload replaced the editor with "this file
    // is unavailable" at the next render — the same teardown, one level down,
    // that the `.catch` below exists to prevent. Cancelling leaves the data in
    // place (`revert: true` is its default). Six other `useFile` callers read
    // this same key, so it is not only the editor's.
    //
    // (Do NOT reason your way back to `removeQueries` on the grounds that the
    // global `file:deleted` handler has already dropped this entry anyway. It
    // often has not: that handler — `patchListsFromFileEvent`, reached only
    // through `useFiles` — returns before the drop whenever the event carries no
    // `parentId`, and `features/files/api/routes.ts` publishes deletes without one
    // (3841, 6992). Two earlier versions of this note tried to list every reason
    // the entry might survive and each got one of them wrong; this is the leg that
    // holds. Cancel, don't drop.)
    //
    // `refetchType: 'none'`: the read below is the one request. Letting the
    // invalidation refetch the active observers as well would just ask twice;
    // they get this read's answer either way (measured), and on a failure they
    // keep the last good data.
    const key = fileKeys.detail(fileId)
    const readAtDeleteSeq = deleteSeqRef.current
    const readAtAccess = accessGenerationNow()
    /** this load's pending re-asks — cleared with it (a file change / unmount) */
    const reaskTimers: Array<ReturnType<typeof setTimeout>> = []
    // A superseded answer: mark the detail stale (the next load must ASK — its cache entry may hold
    // that very answer) and load again after the access throttle's interval (never faster than the
    // rest of the app re-reads during a run of changes).
    // At most SUPERSEDED_REASKS in a row: under a sustained run of access changes (a script granting a
    // team hundreds of folders) every load spans one, and the window would spin for the whole run —
    // then the answer is shown, and the access burst's re-reads correct it (round 31 review).
    const superseded = () => accessGenerationNow() !== readAtAccess && supersededAsksRef.current < SUPERSEDED_REASKS
    const reaskSuperseded = () => {
      supersededAsksRef.current++
      void queryClient.invalidateQueries({ queryKey: key, exact: true, refetchType: 'none' })
      reaskTimers.push(setTimeout(() => { if (!cancelled) setReloadSeq(n => n + 1) }, ACCESS_CHANGE_MIN_INTERVAL_MS))
    }
    const freshRead = reload
      ? queryClient.cancelQueries({ queryKey: key, exact: true })
          .then(() => queryClient.invalidateQueries({ queryKey: key, exact: true, refetchType: 'none' }))
      : Promise.resolve()
    freshRead
      .then(() => fetchAndCacheFileInfo(queryClient, fileId))
      .then(async info => {
        // Decided BEFORE the name lands — the name is what lets the editor mount.
        const pending = await chainAwaitsApprovalInListing(queryClient, info, fileId)
        if (cancelled) return
        // An access change came in during the load: this answer (a "back", a name) may predate a revoke
        // or a bin — show nothing of it (no error cleared, no editor mounted on it) and ask again
        // (#1260, Codex / round 30 reviews).
        if (superseded()) { reaskSuperseded(); return }
        supersededAsksRef.current = 0
        // A load that SUCCEEDS clears a previous failure — this is what brings a
        // window back from "Unable to open" when the file it could not open is
        // restored. Nothing else clears `error`, so a reload that fails again
        // leaves the reader on that screen, which at least has a way out.
        setError(null)
        setChainPending(pending)
        setFileName(info.name)
        setFileKind(info.fileType ?? null)
        // `?? []` and not `if (...)`: breadcrumbs are OPTIONAL on the detail, so
        // skipping the write left the PREVIOUS file's full path on screen when
        // the next one came back without any.
        setFileParentId(info.parentId)
        setBreadcrumbs(info.breadcrumbs ?? [])
        fileIsBack(info, readAtDeleteSeq)
      })
      .catch(err => {
        if (cancelled) return
        // Two different failures, and only one of them is worth protecting an
        // editor from.
        //
        // A NETWORK failure over a live editor stays a toast. The error screen
        // returns before the editor is built, so taking the window over would
        // unmount `EditorHost`, drop the collab room and lose unsaved work — for
        // something as ordinary as a wifi blip. And that is the likely moment: a
        // dropped socket is recovered with a resync SIGNAL and never a replay, so
        // the reload we ask for on reconnect lands exactly when the network is
        // least able to answer it.
        //
        // A 403/404 is not that. The server has ANSWERED: this reader cannot have
        // this file — it was binned, or access was revoked (the two are one reply
        // on purpose). Keeping the editor up then protects nothing, because the
        // work cannot be saved back to a file that is gone; it just lets someone
        // keep typing into a window that is lying to them until a save fails. So
        // the window is taken over, even over a live editor. Told apart by
        // `.status`, which the api client attaches to a real HTTP error and a
        // network failure never has ("A real HTTP error carries `.status`").
        //
        // This narrows a gate rounds 5, 6, 14 and 15 built, and narrows it on
        // purpose: that gate was written against the wifi blip and treated the
        // server's clear "no" as the same thing.
        // #1316: dropped because its session ended (an identity switch; this window stays open) — load again in the
        // new session, never "The session ended…" as the window's error
        if (isSessionEndedError(err)) { if (!cancelled) setReloadSeq(n => n + 1); return }
        // #1210: refused because the identity changed under the load (the pending-chain check gives up after its own
        // re-asks) — not an answer: keep the editor unmounted and load again as the identity now active.
        if ((err as Error | null)?.name === 'IdentityChangedError') {
          // a TENANT change: this file belongs to the other tenant — nothing to load here, nothing shown
          if (!(err as { tenantChanged?: boolean }).tenantChanged) {
            reaskTimers.push(setTimeout(() => { if (!cancelled) setReloadSeq(n => n + 1) }, 0))
          }
          return
        }
        const status = (err as { status?: number } | null)?.status
        const answeredGone = status === 403 || status === 404
        // #1264 — the lists that show this file re-read its folder (it may be gone, its delete never
        // announced). A dedicated channel the list holders hear — never this window, so it can't loop.
        if (answeredGone) reportListedRowRefused(fileId)
        // a refusal asked before an access change that came in during the load (a re-grant) says nothing
        // about now: load again rather than take the window over (#1260, Codex review)
        if (answeredGone && superseded()) { reaskSuperseded(); return }
        // past the cap, a refusal asked across a change is SHOWN — but asked again, at the throttle's pace,
        // until a load spans no change: nothing else re-runs this loader (a re-grant sends no file event),
        // so a window refused mid-re-grant would sit on "Unable to open" for good (round 32 review)
        if (answeredGone && accessGenerationNow() !== readAtAccess) {
          void queryClient.invalidateQueries({ queryKey: key, exact: true, refetchType: 'none' })
          reaskTimers.push(setTimeout(() => { if (!cancelled) setReloadSeq(n => n + 1) }, ACCESS_CHANGE_MIN_INTERVAL_MS))
        } else {
          supersededAsksRef.current = 0
        }
        // #823: already shown as deleted — someone else's strip over the kept editor, or this window's own receipt;
        // a reload answering "gone" (a resync, a late event) confirms it, never takes the window over
        if (answeredGone && deletedShownRef.current) return
        if (reload?.fromLiveEditor && !answeredGone) {
          toastRef.current.error(`Could not reload this file: ${err.message}`)
          return
        }
        setError(errorText(err) ?? '')   // a dropped answer was asked again above
      })
    return () => { cancelled = true; for (const t of reaskTimers) clearTimeout(t) }
  }, [fileId, queryClient, reloadSeq, fileIsBack])

  // #823: this window's editor had its session ended by the server. Asks at once whether the file is already back
  // (a quick restore can land BEFORE the editor's own close — its socket is not the realtime one), and again on every
  // restore event while it stays ended (`probeIfEndedRef`, from the realtime handler below).
  useEffect(() => {
    if (!fileId) return
    editorEndedRef.current = false
    remountedFromRefusalRef.current = false
    let probe = 0
    /** re-asks of a superseded answer — cleared with the effect (a file change / unmount) */
    const probeTimers: Array<ReturnType<typeof setTimeout>> = []
    const ask = () => {
      if (!editorEndedRef.current) return
      const mine = ++probe
      const readAtDeleteSeq = deleteSeqRef.current
      // the SERVER's answer, uncached (as the workspace tab asks): the detail cache is fresh for 30 s, and the entry
      // from before the delete would say "back" for a file in the bin. Not through the cache's fetch either — that
      // would join (or, cancelled, break) a restore reload already out. A "back" answer is written into the cache so
      // the new editor opens straight onto it; a "gone" one leaves the entry the kept editor reads alone.
      // Seeded like `fetchAndCacheFileInfo` seeds them: the new editor reads the permission and breadcrumbs too.
      // Read past an access change that came in during it (#1260): an answer from before it is not
      // stored as fresh — the new editor's own detail / permission queries read it instead.
      void fetchFileInfoPastAccessChanges(fileId).then(({ info, current, identity }) => {
        if (mine !== probe || readAtDeleteSeq !== deleteSeqRef.current || !detailIsBack(info)) return
        if (current) {
          queryClient.setQueryData(fileKeys.detail(fileId), info)
          if (info.effectivePermission) seedPermission(queryClient, fileId, info.effectivePermission, { fromDetail: true, identity, acting: actingOrgEmailOf(info) })
          const crumbs = (info as { breadcrumbs?: Array<{ id: string; name: string }> }).breadcrumbs
          if (crumbs) queryClient.setQueryData(fileKeys.breadcrumbs(fileId), crumbs)
        } else {
          // changes kept arriving through every read: this "back" may predate a revoke or a bin — never
          // act on it (Codex review); ask again in a moment, while the editor is still ended
          void queryClient.invalidateQueries({ queryKey: fileKeys.detail(fileId) })
          void queryClient.invalidateQueries({ queryKey: fileKeys.permission(fileId) })
          probeTimers.push(setTimeout(() => { if (mine === probe) ask() }, ACCESS_CHANGE_MIN_INTERVAL_MS))
          return
        }
        fileIsBack(info, readAtDeleteSeq)
      }, () => { /* gone, or no answer: the editor stays ended, read-only, its last text kept */ })
    }
    probeIfEndedRef.current = () => { remountedFromRefusalRef.current = false; ask() }
    // this window's editor host (hostKey = the file id) says its session ended
    const onEnded = (e: Event) => {
      const d = (e as CustomEvent<EditorSessionEndedDetail>).detail
      if (!d || d.hostKey !== fileId || d.fileId !== fileId) return
      editorEndedRef.current = true
      // a fresh editor refused AGAIN (the detail says yes, the room says no) waits for the next restore / reload —
      // never an ask-remount loop
      if (d.reason === 'refused' && remountedFromRefusalRef.current) return
      ask()
    }
    window.addEventListener(EDITOR_SESSION_ENDED_EVENT, onEnded)
    return () => {
      probe++
      probeIfEndedRef.current = () => {}
      window.removeEventListener(EDITOR_SESSION_ENDED_EVENT, onEnded)
      for (const t of probeTimers) clearTimeout(t)
    }
  }, [fileId, queryClient, fileIsBack])

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showMenu])

  // Listen for file delete / move / rename — refresh breadcrumbs +
  // parentId so the view doesn't show a stale path after the file is
  // moved or renamed out from under us (#113 surface 2). The mount-only
  // fetch above never re-runs; without this branch breadcrumbs and
  // parentId stay frozen at the original path.
  useEffect(() => {
    if (!fileId) return
    return onFileEvent(event => {
      // #823: an ended editor asks again on ANY restore — a pointer's restore names its target, a folder's may name
      // one this window's breadcrumbs don't show
      if (event.type === 'file:restored') probeIfEndedRef.current()
      // #662: a SYNC-DETECTED agent `mv` mints a NEW drive row and reports the
      // move with `previousFileId` = THIS view's (now-binned) old id. Treat it
      // like the pre-#662 `file:deleted(oldId)`: the file moved out from under
      // us, so mark deleted rather than silently showing stale content.
      if (event.type === 'file:moved' && event.previousFileId === fileId && event.previousFileId !== event.fileId) {
        deleteSeqRef.current++
        setDeleted(true)
        return
      }
      // #823: a delete event names only the deleted ROOT — a folder above this file reaches it through the
      // breadcrumbs (refreshed on every move above, #113). Compound ids match by their last segment.
      if (event.type === 'file:deleted' && breadcrumbsRef.current.some(b =>
        b.id !== fileId && (b.id === event.fileId || (b.id.startsWith('s_') && b.id.endsWith(`_${event.fileId}`))))) {
        deleteSeqRef.current++
        setDeleted(true)
        return
      }
      // …and that folder coming back from the bin brings this file back with it: reload as for its own restore
      if (event.type === 'file:restored' && event.fileId !== fileId && breadcrumbsRef.current.some(b =>
        b.id === event.fileId || (b.id.startsWith('s_') && b.id.endsWith(`_${event.fileId}`)))) {
        // the deleted state stays until the reload answers "back" (`fileIsBack`) — the file itself may still be binned
        pendingReloadRef.current = { fromLiveEditor: editorLiveRef.current }
        setReloadSeq(n => n + 1)
        return
      }
      if (event.fileId !== fileId) return
      if (event.type === 'file:deleted') {
        deleteSeqRef.current++
        setDeleted(true)
        return
      }
      // The file came BACK — someone restored it from the bin, including the
      // Ctrl+Z this window's own Delete now records. Without this the undo
      // worked and the window did not notice: the file was in the drive again
      // while the screen still said it was binned and offered only "Go back".
      // Every other viewer listens for this (ImageViewer, PdfViewer, the
      // version panel); this one did not, and only started being able to
      // CAUSE it when Delete landed here.
      if (event.type === 'file:restored') {
        // Nothing is reset here: ASK the loader above to run again. It re-decides
        // everything a restore can change — the name, the pointer kind, whether a
        // restored `.chain` still Needs approval (D24: clearing that flag would
        // open it to a reader nobody approved it for), and whether the file opens
        // at all — and it is the only place that can report a reload that fails,
        // because it owns `error`. `fromLiveEditor` is the LAST RENDER's: the
        // window the reload is asked from is the one still on screen.
        // #823: the deleted state (strip or receipt) is cleared by the reload's "back" answer (`fileIsBack`), not
        // here — a second delete can commit before this reload answers, and its 404 must find the delete on screen.
        pendingReloadRef.current = { fromLiveEditor: editorLiveRef.current }
        setReloadSeq(n => n + 1)
        return
      }
      if (event.type === 'file:moved' || event.type === 'file:updated') {
        // The same ask as the restore branch above, and for the same reasons. This
        // used to run its own fetch and write the answer straight into state, which
        // left it with every hole that branch was taken apart to close:
        //
        //  - no "this window has moved on" guard. The view is reused for the next
        //    file (Save As navigates in place; the desktop route mounts it unkeyed),
        //    so an answer arriving after the swap wrote the OLD file's name, kind and
        //    parent over the new one — the ⋮ confirm naming one file while deleting
        //    another, and the `.shared` access warning decided on the wrong row.
        //  - it never re-asked whether the row is a `.chain` that still Needs
        //    approval, so a `file:updated` that re-points one left this window
        //    following it (D24 says nobody does).
        //  - it kept a stale breadcrumb path when the answer came back without one,
        //    which is optional on the detail.
        //  - invalidating is not a fresh read when a request is already in flight;
        //    the loader cancels first.
        //
        // All four are the loader's, already, and tested there.
        pendingReloadRef.current = { fromLiveEditor: editorLiveRef.current }
        setReloadSeq(n => n + 1)
      }
    })
  }, [fileId, onFileEvent, queryClient])

  /**
   * A socket gap is recovered with a SIGNAL, never a replay — so every file event
   * that happened while this window was disconnected is simply lost to it. Without
   * this the window never re-reads at all: a file deleted during the gap leaves a
   * live editor up over a row that is gone, and the reader finds out when a save
   * fails.
   *
   * One reload answers all of it, because the loader re-decides everything an event
   * could have changed — the name, the pointer kind, D24 approval, and whether the
   * file opens at all.
   *
   * No throttle, deliberately: the server distinguishes the two cases and only the
   * second reaches here. `sync-ack` means nothing was missed and explicitly does NOT
   * call these handlers ("refetching every list on every first connect would undo
   * the whole point of gating recovery on reconnects"); `resync` means it detected a
   * gap, and it adopts the server's counter as it fires, so a second reconnect in the
   * same window does not re-trigger.
   */
  useEffect(() => {
    if (!fileId) return
    return onResync(kind => {
      if (kind !== 'file') return
      pendingReloadRef.current = { fromLiveEditor: editorLiveRef.current }
      setReloadSeq(n => n + 1)
    })
  }, [fileId, onResync])

  /**
   * The other way an event is lost: before the socket is subscribed at all. A fresh
   * tab reads the file first and connects after; a rename landing in between is
   * absorbed by the first connect's baseline (`sync-ack`) and never delivered, so
   * the window kept the old name until a manual refresh (the live-rename e2e caught
   * it on stagetest, 2026-10-10: the rename landed ~200 ms after the socket opened).
   * One re-read per fresh connect — a window opened on an already-live socket gets
   * no baseline and needs none.
   */
  useEffect(() => {
    if (!fileId) return
    return onBaseline(() => {
      pendingReloadRef.current = { fromLiveEditor: editorLiveRef.current }
      setReloadSeq(n => n + 1)
    })
  }, [fileId, onBaseline])

  // Embedded (chat-home side panel) and mobile surfaces host their own
  // container; only the desktop `/file/:id` route runs as a top-level shell
  // window, so only it portals its bar into the shared shell top bar and
  // floats the editor in a ws-card-hero (like AppWindow / Keybook / Settings).
  //
  // Computed HERE, above the early returns, because the failure screens below
  // need the same chrome decision the success path makes at the bottom.
  const embedded = onClose != null
  const useShellBar = !embedded && !mobile

  /** Window chrome for the states that have no file to show.
   *
   *  The `error` and `deleted` branches return long before `titleBar` and
   *  `body` are built, and both of those need a LOADED file (`registration`,
   *  `room`, `ext`, `fileName`) — so those branches cannot simply fall through
   *  to the normal window, and used to render as a bare message with no
   *  chrome at all: no title bar, no close button, nothing.
   *
   *  They get their own minimal bar instead: the same AppTopBar every shell
   *  app uses, with the red close wired to `closeView`, but WITHOUT the file
   *  action cluster. Save, Download, Version History and Reveal in Drive all
   *  operate on a file that is missing or forbidden — a Download button for a
   *  deleted file is worse than no button. */
  const failureShell = (label: string, content: React.ReactNode) => {
    const bar = (
      <AppTopBar
        area="workspace"
        left={
          <span data-testid="singlefile-failure-title" className="text-xs font-medium themed-text truncate select-none cursor-default">
            {label}
          </span>
        }
        onClose={closeView}
        closeTitle="Close"
      />
    )
    if (useShellBar) {
      return (
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <ShellTopBarPortal active>{bar}</ShellTopBarPortal>
          <div className="flex-1 flex min-w-0 min-h-0 overflow-hidden px-3 pb-3 pt-0.5">
            <div className="ws-card-hero flex-1 flex flex-col min-w-0 min-h-0 relative">{content}</div>
          </div>
        </div>
      )
    }
    return (
      <div className={`flex-1 flex flex-col min-h-0 min-w-0 ${closing ? 'mobile-file-out pointer-events-none' : ''}`}>
        {bar}
        {content}
      </div>
    )
  }

  if (!fileId) return null

  // What was reported: a file opened from the chat home's Recent list that had
  // been deleted, or whose permission was revoked (the server deliberately
  // conflates those two — see the comment at DriveView.tsx:2042), left the
  // user on a dead screen with no way out at all.
  //
  // The label says "Unable to open" rather than the filename because there is
  // no trustworthy name here: the fetch that would have supplied it is the one
  // that failed, so `fileName` is empty in this state.
  if (error) {
    return failureShell('Unable to open', (
      <div data-testid="singlefile-error" className="flex-1 flex items-center justify-center themed-text-muted">
        <div className="text-center">
          <FileQuestion size={48} className="mx-auto mb-4 opacity-50" />
          <p>Failed to load file</p>
          <p className="text-sm opacity-60 mt-1">{error}</p>
          {/* Kept ALONGSIDE the window's close button, not replaced by it: this
              is the obvious affordance at the centre of the screen the reader
              is already looking at. */}
          <button
            onClick={closeView}
            data-testid="singlefile-error-back"
            className="mt-4 px-4 py-2 text-sm themed-btn-primary rounded transition-colors"
          >
            Go back
          </button>
        </div>
      </div>
    ))
  }

  if (!fileName) return LoadingSpinner

  // Unlike the error branch this one HAS a filename — the file loaded fine and
  // was deleted from under the reader — so the bar can name it.
  const deletedHere = askedToDeleteRef.current === fileId
  // #823: somebody ELSE deleting the file (or a folder above it) keeps the editor mounted under a strip — its last
  // text stays on screen to read and copy (a live editor adds its own "moved to the bin by …" notice). Your own
  // delete is a receipt: the full screen below.
  if (deleted && deletedHere) {
    return failureShell(fileName, (
      <div data-testid="singlefile-deleted" className="flex-1 flex items-center justify-center themed-text-muted">
        <div className="text-center">
          <Trash2 size={48} className="mx-auto mb-4 opacity-50" />
          {/* The receipt of your OWN delete says where it went, which is also how to get it back. Somebody
              ELSE deleting it is the strip over the kept editor (#823, `deletedStrip`). */}
          <p className="themed-text mb-1">File deleted</p>
          <p className="text-sm opacity-60">{`"${fileName}" is in the bin.`}</p>
          {/* `closeView`, not a second spelling of it: the inline
              `onClose ? onClose() : navigate(-1)` this replaces was missing
              BOTH the mobile exit animation and the no-history fallback. */}
          <button
            onClick={closeView}
            data-testid="singlefile-deleted-back"
            className="mt-4 px-4 py-2 text-sm themed-btn-primary rounded transition-colors"
          >
            Go back
          </button>
        </div>
      </div>
    ))
  }

  if (chainPending) {
    return failureShell(fileName, (
      <div data-testid="singlefile-chain-needs-approval" className="flex-1 flex items-center justify-center themed-text-muted">
        <div className="text-center max-w-sm px-4">
          <AlertTriangle size={40} className="mx-auto mb-4 text-amber-400" />
          <p className="themed-text mb-1">{t('driveView.chain.needsApprovalTitle')}</p>
          <p className="text-sm opacity-70">{t('driveView.chain.needsApprovalBody')}</p>
          <button
            onClick={closeView}
            data-testid="singlefile-chain-needs-approval-back"
            className="mt-4 px-4 py-2 text-sm themed-btn-primary rounded transition-colors"
          >
            {t('driveView.chain.needsApprovalOk')}
          </button>
        </div>
      </div>
    ))
  }

  const ext = getFileExt(fileName)

  // Full breadcrumb path — surfaced only as the filename's hover tooltip now
  // (the title bar shows the bare filename, app-window style). `breadcrumbs`
  // ends with the file itself, so the join yields the complete "Drive / … /
  // file" path. Falls back to the bare name if breadcrumbs haven't loaded.
  const breadcrumbPath = breadcrumbs.length > 0
    ? breadcrumbs.map((b) => b.name).join('  /  ')
    : fileName

  // `embedded` / `useShellBar` are computed above the early returns — the
  // failure screens need the same decision. See their docblock there.

  // The window title bar — the SAME AppTopBar every shell app uses: filename
  // (bare) + its ⋮ menu on the left, theme toggle + red close on the right.
  // Hovering the filename reveals the full breadcrumb path (Papan tooltip).
  const titleBar = (
    <AppTopBar
      area="workspace"
      left={
        <div data-testid="singlefile-breadcrumbs" className="flex items-center gap-1 min-w-0">
          <Tooltip content={breadcrumbPath} always placement="bottom" className="min-w-0 truncate">
            <span className="text-xs font-medium themed-text truncate select-none cursor-default">{fileName}</span>
          </Tooltip>
          {isDirty && <span className="text-[10px] themed-text-muted shrink-0">(unsaved)</span>}
          {/* More menu — docked directly to the filename, which is where
              this shell puts it: AppWindow's own title bar comments say "right
              after the file name". Moving it in with the window controls put a
              per-FILE menu inside a per-WINDOW cluster. */}
          {/* #823: none of these acts on a file in the bin (Save, Download, Reveal, History, Delete would all fail —
              a Delete would even show "you deleted it") — hidden while it is deleted by someone else */}
          <div className="relative shrink-0" ref={menuRef} style={deleted && !deletedHere ? { display: 'none' } : undefined}>
            <button
              onClick={() => setShowMenu(s => !s)}
              aria-label="More options"
              data-tooltip="More options"
              className="ws-card-iconbtn"
            >
              <MoreVertical size={14} />
            </button>
          {showMenu && (
            <div className="absolute left-0 top-full mt-1 themed-bg-secondary themed-border border rounded-lg shadow-xl py-1 z-[9999] min-w-[160px]">
              <button
                onClick={() => { saveRef.current?.(); setShowMenu(false) }}
                // Not while anything covers the editor (`editorCovered`). This menu sits in the
                // top bar, outside the overlay, so it is the mouse route to the
                // same invisible write the Cmd+S guard closes.
                disabled={editorCovered || !isDirty}
                className={`w-full px-3 py-1.5 text-left text-xs transition-colors flex items-center gap-2 ${
                  isDirty && !editorCovered ? 'themed-text hover:themed-bg-tertiary' : 'themed-text-muted cursor-not-allowed'
                }`}
              >
                <Save size={13} />
                <span className="flex-1">{isDirty ? 'Save' : 'Saved'}</span>
                <span className="text-[10px] themed-text-muted">{formatShortcut({ key: 's', mod: true })}</span>
              </button>
              <button
                onClick={() => { setShowSaveAs(true); setShowMenu(false) }}
                // Save As copies the CURRENT document. Offered while a preview
                // covers it, the user asks for a copy of what they are reading
                // and gets a copy of something else, with nothing in the dialog
                // naming which version it took. Restore, in the preview's own
                // bar, is the control for wanting the old one.
                disabled={editorCovered}
                className={`w-full px-3 py-1.5 text-left text-xs transition-colors flex items-center gap-2 ${
                  editorCovered ? 'themed-text-muted cursor-not-allowed' : 'themed-text hover:themed-bg-tertiary'
                }`}
              >
                <Save size={13} />
                <span className="flex-1">Save As...</span>
              </button>
              <button
                onClick={() => { discardRef.current?.(); setShowMenu(false) }}
                // Not while a preview covers the editor, for the same reason as
                // Save beside it: it throws away changes to a file the user
                // cannot see. The preview has its OWN Discard when the version
                // being read is the latest — that one is deliberate, and it
                // closes the preview as it goes.
                disabled={editorCovered || !isDirty}
                className={`w-full px-3 py-1.5 text-left text-xs transition-colors flex items-center gap-2 ${
                  isDirty && !editorCovered ? 'text-red-400 hover:themed-bg-tertiary' : 'themed-text-muted cursor-not-allowed'
                }`}
              >
                <RotateCcw size={13} />
                <span className="flex-1">Discard Changes</span>
              </button>
              <div className="border-t themed-border my-0.5" />
              {/* History lives HERE, not in the editor.
                  It used to be delegated to the editor via `onShowHistory` —
                  but only HtmlEditor implements that prop, so every other type
                  (md/txt/json, docx/xlsx, sqlite/parquet, images) lost the
                  ability to view or restore prior versions entirely, and the
                  read-only registrations never got the prop at all. This view
                  already owns `showVersionPanel`, so driving it from its own ⋯
                  is editor-agnostic and restores parity for every file type in
                  one place. The editor's own History button (HtmlEditor) still
                  works — it drives the same state. */}
              {/* App-level "App history" is intentionally NOT here — a
                  single-file view is always file-level, so it only ever shows
                  the file's own History. The PDApp's app-level history lives on
                  the app's own tab in WorkspaceView (#612 UX fix). */}
              <button
                onClick={() => { setPreviewVersionId(null); setShowVersionPanel(v => !v); setShowMenu(false) }}
                className={`w-full px-3 py-1.5 text-left text-xs hover:themed-bg-tertiary transition-colors flex items-center gap-2 ${
                  showVersionPanel ? 'themed-text font-medium' : 'themed-text'
                }`}
              >
                <Clock size={13} />
                <span className="flex-1">Version History</span>
              </button>
              <div className="border-t themed-border my-0.5" />
              <button
                onClick={() => {
                  // The file's REAL parent folder — not breadcrumbs[last], which
                  // is the file itself (root→file order); using it navigated to
                  // /folder/<fileId>, opening the file "as a folder". The drive
                  // root's parentId is the `papan_<tenant>` sentinel → the id-less
                  // /folder route. `?reveal=` selects + scrolls to the file.
                  const parentId = fileParentId && !fileParentId.startsWith('papan_') ? fileParentId : null
                  navigate(parentId
                    ? `${urlPrefix}/folder/${parentId}?reveal=${fileId}`
                    : `${urlPrefix}/folder?reveal=${fileId}`)
                  setShowMenu(false)
                }}
                className="w-full px-3 py-1.5 text-left text-xs themed-text hover:themed-bg-tertiary transition-colors flex items-center gap-2"
              >
                <FolderSearch size={13} />
                <span className="flex-1">Reveal in Drive</span>
              </button>
              <button
                onClick={() => {
                  filesApi.download([fileId])
                  setShowMenu(false)
                }}
                className="w-full px-3 py-1.5 text-left text-xs themed-text hover:themed-bg-tertiary transition-colors flex items-center gap-2"
              >
                <Download size={13} />
                <span className="flex-1">Download</span>
              </button>
              {/* Last, red, behind a rule — where the drive puts it, because a
                  destructive item next to Download is one slip away. */}
              <div className="border-t themed-border my-0.5" />
              <button
                onClick={() => { setShowMenu(false); void deleteFile() }}
                className="w-full px-3 py-1.5 text-left text-xs text-red-400 hover:themed-bg-tertiary transition-colors flex items-center gap-2"
              >
                <Trash2 size={13} />
                <span className="flex-1">Delete</span>
              </button>
            </div>
          )}
          </div>
        </div>
      }
      right={
        /* Window-level actions, away from the filename. They used to sit
           inside the breadcrumb row, where a labelled button read as more
           caption text beside the name — and competed with it for width,
           since the name truncates and these do not. */
        <div className="flex items-center gap-1">
          {/* The one thing this window cannot do for you. */}
          <button
            onClick={openAsProject}
            data-testid="singlefile-open-as-project"
            data-tooltip="Open this file in a project workspace, with the AI assistant"
            /* `ws-tool-btn` is this shell's shape for a small LABELLED action
               (the home screen's Upload buttons are the same thing), and
               `-accent` is its primary variant: a solid fill, no border, white
               text. Both tried first and rejected for being too close to the
               bar: a hairline border, which at this size read as a box drawn
               around text, and `ws-pill` (the tab shape), which is tuned to
               sit on the tab strip. NOT ws-card-iconbtn either — that is a
               fixed 28x28 icon box a label spills out of. */
            className="ws-tool-btn ws-tool-btn-accent shrink-0 text-[11px] whitespace-nowrap cursor-pointer"
          >
            {/* No colour of its own: the accent button is a solid fill with
                white text, and the old purple icon sat on top of it almost
                invisibly. It inherits, so it matches the label. */}
            <LayoutGrid size={13} />
            Open as AI Project
          </button>
          {showProjectPicker && fileId && (
            <OpenAsProjectDialog
              // Remount on a file change. The dialog never resets `projects` /
              // `wrap` / `loading` when its `fileId` prop changes, so without
              // this it kept showing the PREVIOUS file's rows, interactive —
              // and clicking one opened the old file's project with the NEW
              // file's id in the query string, landing in a workspace that
              // cannot reach it. The embedded chat-home host already remounts
              // this way; the desktop route reuses one instance across params.
              key={fileId}
              fileId={fileId}
              fileName={fileName ?? 'this file'}
              onOpenProject={openExistingProject}
              onWrapFolder={wrapFolderAsProject}
              onCreateNew={createProjectForFile}
              onClose={() => setShowProjectPicker(false)}
            />
          )}
        </div>
      }
      onClose={closeView}
      closeTitle="Close"
    />
  )

  // Editor + version panel — the window's actual content.
  const body = (
    <div className="flex-1 overflow-hidden min-h-0 flex flex-row relative">
        {/* Editor content */}
        {/* #900 — shortcuts from the keyboard only while this view is on screen (the chat home keeps it mounted). */}
        <CommandRegistryProvider keyboard={deckActive}>
        {/* #929/#930 — the editor's commands resolve disabled while anything covers it (EditorCover). Editors here
            register under the parent id 'editor', which nobody registers in this view; a cover on it still counts. */}
        <CoverCommandScope scopeId="editor" covered={editorCovered} />
        <EditorCoverProvider onChange={setEditorCover} covered={editorCovered}>
        <div className="flex-1 min-w-0 overflow-hidden relative">
          {/* absolute inset-0 (mirrors MobileWorkspaceScreen's editor stack):
              the editor is taken OUT of flow so the sandbox's intrinsic width
              can't push this column wide — it fills the column's flex-sized box
              regardless of content, so the preview shrinks with the viewport. */}
          {/* Same viewer attributes the workspace TAB stamps (TabContent). The
              testid names the ROLE — "the viewer mounted for this file" — and
              both surfaces fill it; sharing it means the file-viewer routing
              suite covers this window too, which matters now that a drive
              double-click lands here rather than in a tab. */}
          {/* #942 — the same host the workspace tab mounts through: which editor,
              read-only (live), the size limit, the room. It stamps the viewer
              attributes (`tab-viewer`) both surfaces share.
              Mounted as soon as the name is known, without gating on its own
              queries beyond their first answer (the detail just fetched above
              seeds the permission): a file the caller LOSES access to while
              open keeps its editor, and the server's 4403 close still drives
              the editor's "no longer have access" overlay
              (active-org-email-ui.spec.ts). */}
          <div className="absolute inset-0 flex flex-col">
            <EditorHost
              key={`${fileId}:${restoredSeq}:${identityEpoch}`}
              ref={editorRef}
              fileId={fileId}
              fileName={fileName}
              fileType={ext}
              tenantId={tenantId}
              hostKey={fileId}
              preferredEditorId={settings.preferredEditors?.[ext]}
              isActive
              onDirtyChange={handleDirtyChange}
              saveRef={saveRef}
              discardRef={discardRef}
              registerUndoRedo={registerUndoRedo}
              onShowHistory={() => { setPreviewVersionId(null); setShowVersionPanel(prev => !prev) }}
              historyActive={showVersionPanel}
              revealLine={revealLine}
              onClose={closeView}
              // #823: deleted by someone else — keep the last detail (the editor stays); the strip above is the notice
              deleted={deleted && !deletedHere}
              deletedNoticeByHost={false}
            />
          </div>

          {/* Version preview overlay */}
          {previewVersionId && showVersionPanel && (
            <div className="absolute inset-0 z-10 themed-bg-primary flex flex-col overflow-hidden">
              <VersionPreview
                key={identityEpoch}
                fileId={fileId}
                fileName={fileName}
                versionId={previewVersionId}
                onBack={() => setPreviewVersionId(null)}
                isLatest={previewIsLatest}
                isDirty={isDirty && !editorCover}
                onDiscard={() => { if (!editorCoverRef.current) discardRef.current?.() }}
              />
            </div>
          )}
        </div>

        {/* Version history panel — a w-72 side panel on desktop; on a phone it
            docks to the right at 40% width (overlaying the editor) rather than
            taking the full screen. */}
        {showVersionPanel && (
          <div className={`overflow-hidden border-l themed-border ${mobile ? 'absolute inset-y-0 right-0 z-20 w-2/5 themed-bg-primary' : 'w-72 shrink-0'}`}>
            <VersionPanel
              fileId={fileId}
              fileName={fileName}
              tenantId={tenantId}
              onClose={() => { setShowVersionPanel(false); setPreviewVersionId(null) }}
              onPreview={(versionId, isLatest) => {
                setPreviewVersionId(versionId || null)
                setPreviewIsLatest(isLatest)
              }}
              selectedVersion={previewVersionId}
              isDirty={isDirty && !editorCover}
              onDiscard={() => { if (!editorCoverRef.current) discardRef.current?.() }}
              onManageRetention={() => navigate(`${urlPrefix}/settings/version-retention?file=${fileId}`)}
            />
          </div>
        )}
        </EditorCoverProvider>
      </CommandRegistryProvider>
      </div>
  )

  const saveAsDialog = showSaveAs && fileName && (
    <SaveAsDialog
      tenantId={tenantId}
      fileId={fileId}
      defaultName={fileName}
      defaultFolderId={fileParentId}
      onSaved={(newFileId) => {
        setShowSaveAs(false)
        // Embedded (prop-driven) mode has no URL to swap — hand the new
        // file to the host panel instead of navigating away from it.
        if (fileIdProp) { onSwitchFile?.(newFileId); return }
        // Replace — we're already in a file view, so the old file "closes"
        // (one-file-at-a-time; no back-stack pile-up).
        navigate(`${urlPrefix}/file/${newFileId}`, { replace: true })
      }}
      // #929/#930 — refused if the editor became covered while the dialog was open (it copies the live document).
      saveBlockedReason={() => editorCoverRef.current?.message ?? (previewOpenRef.current ? tCollab(PREVIEW_SAVE_BLOCKED_KEY) : null)}
      onClose={() => setShowSaveAs(false)}
    />
  )

  // #823: deleted by someone else while open — the window keeps its content (see the receipt branch above).
  const deletedStrip = deleted && !deletedHere ? (
    <div data-testid="singlefile-deleted" role="status" className="flex items-center gap-3 px-3 py-2 text-sm themed-bg-secondary themed-text border-b themed-border shrink-0">
      <Trash2 size={16} className="shrink-0 opacity-70" />
      <div className="min-w-0 flex-1">
        <span className="font-medium">File has been deleted</span>
        <span className="themed-text-muted">{` — "${fileName}" was deleted while you had it open.`}</span>
      </div>
      <button onClick={closeView} data-testid="singlefile-deleted-back" className="px-3 py-1 text-xs themed-btn-primary rounded transition-colors shrink-0">
        Go back
      </button>
    </div>
  ) : null

  // Desktop `/file/:id` route: run as a top-level shell window — portal the
  // AppTopBar into the shared shell top bar, float the editor in a ws-card-hero
  // on the app stage (px-3 pb-3 gutter, matching Keybook / Settings / AppWindow).
  if (useShellBar) {
    return (
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <ShellTopBarPortal active>{titleBar}</ShellTopBarPortal>
        <div className="flex-1 flex min-w-0 min-h-0 overflow-hidden px-3 pb-3 pt-0.5">
          <div className="ws-card-hero flex-1 flex flex-col min-w-0 min-h-0 relative">
            {deletedStrip}
            {body}
          </div>
        </div>
        {saveAsDialog}
      </div>
    )
  }

  // Embedded (chat-home panel — host already provides the ws-card-hero) and
  // mobile (full-screen): render the bar inline above the body.
  // min-w-0 is LOAD-BEARING: this is a flex item inside a flex-row container,
  // so without it the root keeps its content's intrinsic width and the
  // editor/preview sandbox overflows instead of shrinking with the viewport.
  return (
    <div className={`flex-1 flex flex-col min-h-0 min-w-0 ${closing ? 'mobile-file-out pointer-events-none' : ''}`}>
      {titleBar}
      {deletedStrip}
      {body}
      {saveAsDialog}
    </div>
  )
}
